import { CUE_IDS, type CueId, type CueParams } from '@sol-pool/engine-physics'
import { paramsDePontos, type Pontos } from './escala'
import { NIVEIS_ZERO, type Niveis } from './niveis'
import { pontosDoTaco } from './pontos'
import { RULESET_V3, type PerfilDeTaco, type Ruleset } from './ruleset'

/**
 * A versão GRATUITA de cada arquétipo.
 *
 * São entradas do MESMO modelo dos NFTs — raridade `gratuito`, sem seed —, e
 * não um catálogo à parte: é o que garante que uma coleção externa não seja um
 * caso especial, porque nem a nossa é.
 *
 * A raridade era `common` até 2026-09-13. Mudou porque o taco de graça tem de
 * ser PIOR que o comum cunhado (195 contra 207): enquanto ele era um common,
 * cunhar o item mais barato da coleção dava exatamente o taco que o jogador já
 * tinha, e o primeiro degrau do mint não vendia nada.
 *
 * Os quatro continuam aqui, e vão continuar: todo arquétipo tem a sua versão
 * gratuita, e é dela que a partida de TREINO, o hotseat e o replay tiram o taco
 * quando não há servidor dizendo qual é. O que MUDOU é quem é dado de graça —
 * ver `TACO_GRATUITO_PADRAO`.
 */
export const TACOS_GRATUITOS: Record<CueId, PerfilDeTaco> = Object.fromEntries(
  CUE_IDS.map((id) => [id, { raridade: 'gratuito', arquetipo: id, seed: 0, geracao: 0 } satisfies PerfilDeTaco]),
) as Record<CueId, PerfilDeTaco>

/**
 * O ÚNICO taco que o servidor concede de graça.
 *
 * `TACOS_GRATUITOS` é o modelo (as quatro commons existem); esta constante é a
 * REGRA DE PRODUTO: só o padrão vem junto com a carteira. `pesado`, `preciso` e
 * `efeito` existem apenas como NFT, sorteados no mint — se os quatro fossem
 * dados, o mint não teria o que oferecer a quem já tem tudo, e a raridade do
 * arquétipo não valeria nada.
 *
 * Mora aqui, e não no servidor, porque é o pacote que descreve o modelo; quem
 * a aplica é `apps/server/src/tacos.ts`, que é quem autoriza taco.
 */
export const TACO_GRATUITO_PADRAO: CueId = 'padrao'

/** Perfil → bps, presos. É a única porta entre o modelo e a física. */
export function atributosDoTaco(perfil: PerfilDeTaco, ruleset: Ruleset = RULESET_V3, niveis: Niveis = NIVEIS_ZERO): CueParams {
  return paramsDePontos(pontosDoTaco(perfil, ruleset, niveis))
}

/**
 * Os pontos do taco GRÁTIS padrão.
 *
 * Saem do ruleset, como sempre saíram: é a distribuição da classe `padrao`
 * com o orçamento `gratuito`. A constante existe porque quem calcula pontos
 * por outro modelo (`gdm-pontos.ts`) precisa do PISO, e derivá-lo de novo em
 * cada lugar deixaria os dois podendo discordar.
 *
 * O Bunker propôs no PR #27 trocar isto por 33 em tudo, para o grátis virar o
 * degrau de baixo de uma escada que vai até o full. Ficou de FORA por decisão
 * do Marcos (2026-09-25): mexeria no taco de TODO jogador, inclusive em
 * partida valendo SOL — tacada ~5% mais leve, giro e aderência ~3,5% menores.
 */
export const PONTOS_DO_GRATUITO: Pontos = pontosDoTaco(TACOS_GRATUITOS.padrao, RULESET_V3)

/**
 * O padrão DO JOGO: o que vale em todo fallback do servidor.
 *
 * NÃO é o `DEFAULT_CUE` da física, que continua sendo, ponto a ponto, o COMUM
 * cunhado (140 = `pontosDeParams(DEFAULT_CUE)`): este é o grátis de
 * `PONTOS_DO_GRATUITO`.
 *
 * A MIRA não entra mais nessa lista. Ela era o quinto atributo e o gratuito a
 * tinha mais grossa; desde a física v6 todo taco aponta na mesma resolução, e
 * quem joga de graça mira exatamente como quem cunhou um lendário. O que
 * distingue os tacos é peso, efeito, aderência e quebra.
 *
 * O que este valor descreve é o taco com que se entra na mesa sem ter cunhado
 * nada.
 */
export const PADRAO_DO_JOGO: CueParams = paramsDePontos(PONTOS_DO_GRATUITO)
